import React from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';

import { outputIfNoVariables } from '../../utilities/misc';
import { rem } from '../../utilities/style';
import { colors } from '../../theme/colors';
import { media } from '../../theme/media';
import { headers } from '../../styles/type';
import { dotPatternStyles } from '../../styles/backgrounds';
import { containerStyles } from '../../styles/layout';

import Button from '../buttons/Button';
import ButtonGroup from '../ButtonGroup';
import ButtonText from '../buttons/ButtonText';
import TextRenderer from '../TextRenderer';
import Background from '../Background';

const StyledBackground = styled(Background)`
  padding: ${rem(32)} 0;
  color : ${({ color }) => (color === 'Blue' ? colors.white : colors.blue['800'])};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}

  ${media('>tiny')} {
    padding: ${rem(40)} 0;
  }

  ${ButtonGroup} {
    padding-top: ${rem(40)};
    margin-bottom: ${rem(8)};
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.small}
  position: relative;
  z-index: 1;
  text-align: center;

  ${({ backgroundPattern }) => !backgroundPattern && `
    &::before,
    &::after {
      ${dotPatternStyles}
      content: '';
      position: absolute;
      z-index: -1;
      top: 0;
      width: ${rem(90)};
      height: 100%;
      ${media('>smedium')} {
        width: ${rem(180)};
      }
      ${media('>smedium')} {
        width: 50vw;
      }
    }

    &::before {
      right: 0;
      ${media('>smedium')} {
        right: 100%;
      }
    }
    &::after {
      left: 0;
      ${media('>smedium')} {
        left: 100%;
      }
    }
  `}
`;

const Headline = styled.h2`
  ${headers.h3}
`;

const Cta = ({
  backgroundColor = 'White',
  backgroundPattern,
  headline,
  primaryCtaText,
  primaryCtaLink,
  secondaryCtaText,
  secondaryCtaLink,
  targetBlank,
}) => {
  const darkMode = useTheme().pageOverrides?.includes('Dark Mode');
  return (
    <StyledBackground
      color={backgroundColor}
      pattern={backgroundPattern}
      patternVariation={null}
    >
      <ContentWrapper backgroundPattern={backgroundPattern}>
        {headline && (
          <Headline>
            <TextRenderer text={headline} />
          </Headline>
        )}
        <ButtonGroup stacked>
          {primaryCtaText && primaryCtaLink && (
            <Button
              targetBlank={targetBlank}
              light={backgroundColor === 'Blue' && !darkMode}
              to={primaryCtaLink}
              aria-label={outputIfNoVariables(`${headline} — ${primaryCtaText}`)}
            >
              <TextRenderer text={primaryCtaText} />
            </Button>
          )}
          {secondaryCtaText && secondaryCtaLink && (
            <ButtonText
              color={backgroundColor === 'Blue' || darkMode ? 'light' : 'dark'}
              to={secondaryCtaLink}
              aria-label={outputIfNoVariables(`${headline} — ${secondaryCtaText}`)}
            >
              <TextRenderer text={secondaryCtaText} />
            </ButtonText>
          )}
        </ButtonGroup>
      </ContentWrapper>
    </StyledBackground>
  );
};

Cta.propTypes = {
  headline: PropTypes.string,
  primaryCtaText: PropTypes.string,
  primaryCtaLink: PropTypes.string,
  secondaryCtaText: PropTypes.string,
  secondaryCtaLink: PropTypes.string,
  targetBlank: PropTypes.bool,
  backgroundColor: PropTypes.oneOf(['Blue', 'White', 'Light Blue']),
  backgroundPattern: PropTypes.oneOf(['Large Dots', 'Splash Dots', 'Splash'])
};

export const query = graphql`
  fragment CtaFragment on ContentfulCta {
    id
    __typename
    headline
    primaryCtaText
    primaryCtaLink
    secondaryCtaText
    secondaryCtaLink
    backgroundColor
    backgroundPattern
    targetBlank
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Cta;
